import { ApplicationView } from "@mixitone/mvc";
import React, { useCallback, useEffect, useState } from "react";
import { WizardContent } from "./WizardContent";
import { WizardFooter } from "./WizardFooter";
import { WizardProgressBar } from "./WizardProgressBar";

export interface WizardStep {
  name: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
  validate?: () => boolean;
}

export interface WizardProps {
  steps: WizardStep[];
  onComplete: () => void;
  saving?: boolean;
  doneButtonText?: string;
  onBeforeValidate?: (stepIndex: number) => void;
  onStepChange?: (stepIndex: number) => void;
  initialStep?: number;
}

const ControlledWizard: React.FC<WizardProps> = ({
  steps,
  onComplete,
  saving = false,
  doneButtonText = "Done",
  onBeforeValidate,
  onStepChange,
  initialStep = 0,
}) => {
  const [step, setStep] = useState(() => {
    if (steps.length === 0) return 0;
    return Math.min(Math.max(0, initialStep), steps.length - 1);
  });

  const validateCurrentStep = useCallback(() => {
    const currentStep = steps[step];
    if (!currentStep.validate) {
      return true;
    }
    const result = currentStep.validate();
    return result;
  }, [steps, step]);

  const nextStep = useCallback(() => {
    const currentStep = steps[step];
    // Call onBeforeValidate before validating to allow tracking validation attempts
    if (onBeforeValidate) {
      onBeforeValidate(step);
    }
    const isValid = !currentStep.validate || currentStep.validate();

    if (isValid) {
      if (step < steps.length - 1) {
        const newStep = step + 1;
        setStep(newStep);
        if (onStepChange) {
          onStepChange(newStep);
        }
      } else if (step === steps.length - 1) {
        onComplete();
      }
    }
    // If validation failed, onBeforeValidate was already called to mark as attempted
  }, [steps, step, onComplete, onBeforeValidate, onStepChange]);

  // Add keyboard event listener for Enter key to navigate to next step
  useEffect(() => {
    const abortController = new AbortController();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const target = event.target instanceof HTMLElement ? event.target : null;

        // Don't trigger on textareas (allow multi-line input)
        if (target && target.tagName === "TEXTAREA") {
          return;
        }

        // Check if validation passes before proceeding
        if (validateCurrentStep()) {
          // Prevent default form submission behavior
          event.preventDefault();
          event.stopPropagation();
          nextStep();
        } else {
          // Mark as attempted when Enter is pressed and validation fails
          if (onBeforeValidate) {
            onBeforeValidate(step);
          }
          // Prevent default but don't proceed
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, { signal: abortController.signal });

    return () => {
      abortController.abort();
    };
  }, [validateCurrentStep, nextStep]);

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  return (
    <div
      className="flex flex-col h-full"
      data-wizard
      data-wizard-total-steps={steps.length}
      data-wizard-current-step={step}
    >
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid w-full grid-cols-1 grid-rows-[auto_1fr] min-h-full">
          <WizardProgressBar steps={steps} currentStep={step} onChangeStep={(i) => setStep(i)} />
          <WizardContent
            title={steps[step].title}
            subtitle={steps[step].subtitle}
            stepIndex={step}
            stepName={steps[step].name}
          >
            {steps[step].content}
          </WizardContent>
        </div>
      </div>
      <div className="flex-shrink-0 bg-white border-t border-gray-200">
        <WizardFooter
          onNext={nextStep}
          onBack={prevStep}
          canGoBack={step > 0}
          canGoNext={validateCurrentStep()}
          isLastStep={step === steps.length - 1}
          saving={saving}
          doneButtonText={doneButtonText}
        />
      </div>
    </div>
  );
};

export const Wizard = ApplicationView(ControlledWizard);
